import { useState, useEffect } from "react";

// Full-screen CRPL Loading Screen with login-style background and Y-axis rotation
const CRPLLoadingScreen = ({ message = "Loading your data..." }) => {
  const [loadingText, setLoadingText] = useState(message);

  useEffect(() => {
    const texts = [message, "Crunching numbers...", "Almost ready..."];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % texts.length;
      setLoadingText(texts[index]);
    }, 800);
    return () => clearInterval(interval);
  }, [message]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden">
      {/* Same background as login page */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&q=80')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" />
      
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center">
        <div 
          className="bg-white/95 p-6 md:p-8 rounded-3xl shadow-2xl"
          style={{ perspective: '1000px' }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_chandan-sales-ai/artifacts/tu9ehfss_askaran_binraj-removebg-preview.png"
            alt="CRPL Logo"
            className="w-32 h-32 md:w-40 md:h-40 object-contain animate-spin-y"
          />
        </div>
        <p className="mt-8 text-4xl md:text-5xl font-bold text-white tracking-[6px] font-['Outfit'] drop-shadow-lg">
          CRPL PULSE
        </p>
        <p className="mt-5 text-lg md:text-xl text-white/80">
          {loadingText}
        </p>
      </div>
    </div>
  );
};

// Inline loading overlay with animated logo (no caption)
const InlineLoader = () => (
  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-40 rounded-lg">
    <div 
      className="bg-white p-3 rounded-xl shadow-lg"
      style={{ perspective: '1000px' }}
    >
      <img
        src="https://customer-assets.emergentagent.com/job_chandan-sales-ai/artifacts/tu9ehfss_askaran_binraj-removebg-preview.png"
        alt="Loading"
        className="w-16 h-16 object-contain animate-spin-y"
      />
    </div>
  </div>
);

// KPI Card Skeleton for loading state
const KPICardSkeleton = () => (
  <div className="relative overflow-hidden rounded-xl p-4 bg-white/80 backdrop-blur-xl border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-pulse">
    <div className="flex items-start justify-between mb-2">
      <div className="w-8 h-8 bg-gray-200 rounded-lg" />
    </div>
    <div className="w-16 h-3 bg-gray-200 rounded mb-2" />
    <div className="w-20 h-6 bg-gray-200 rounded mb-2" />
    <div className="w-12 h-4 bg-gray-200 rounded" />
  </div>
);

// Table Skeleton for loading lists/tables
const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <div className="w-full animate-pulse">
    {/* Header */}
    <div className="flex gap-4 p-4 bg-gray-100 rounded-t-lg">
      {Array(columns).fill(0).map((_, i) => (
        <div key={i} className="flex-1 h-4 bg-gray-300 rounded" />
      ))}
    </div>
    {/* Rows */}
    {Array(rows).fill(0).map((_, rowIdx) => (
      <div key={rowIdx} className="flex gap-4 p-4 border-b border-gray-100">
        {Array(columns).fill(0).map((_, colIdx) => (
          <div key={colIdx} className="flex-1 h-4 bg-gray-200 rounded" />
        ))}
      </div>
    ))}
  </div>
);

// Chart Skeleton for loading charts
const ChartSkeleton = ({ height = 300 }) => (
  <div className="w-full animate-pulse" style={{ height }}>
    <div className="flex items-end justify-around h-full gap-2 p-4">
      {[60, 80, 45, 90, 70, 55, 85, 40, 75, 65].map((h, i) => (
        <div
          key={i}
          className="flex-1 bg-gray-200 rounded-t-lg"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  </div>
);

// Card Grid Skeleton
const CardGridSkeleton = ({ cards = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
    {Array(cards).fill(0).map((_, i) => (
      <div key={i} className="p-4 bg-white rounded-xl border border-gray-100">
        <div className="w-24 h-4 bg-gray-200 rounded mb-3" />
        <div className="w-full h-6 bg-gray-200 rounded mb-2" />
        <div className="w-3/4 h-4 bg-gray-200 rounded" />
      </div>
    ))}
  </div>
);

// Empty State Component
const EmptyState = ({ 
  icon: Icon, 
  title = "No data available", 
  description = "There's nothing to show here yet.",
  action,
  actionLabel = "Refresh",
  secondaryAction,
  secondaryActionLabel
}) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-testid="empty-state">
    {Icon && (
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Icon size={32} className="text-gray-400" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 max-w-md mb-4">{description}</p>
    {action && (
      <button
        onClick={action}
        data-testid="empty-state-action-btn"
        className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] transition-colors"
      >
        {actionLabel}
      </button>
    )}
    {secondaryAction && secondaryActionLabel && (
      <button
        onClick={secondaryAction}
        data-testid="empty-state-secondary-btn"
        className="mt-3 px-4 py-2 text-sm font-medium text-[#1B5E20] bg-white border border-[#1B5E20]/30 rounded-lg hover:bg-[#E8F5E9] transition-colors"
      >
        {secondaryActionLabel}
      </button>
    )}
  </div>
);

// Error State Component
const ErrorState = ({
  title = "Something went wrong",
  description = "We couldn't load this data. Please try again.",
  action,
  actionLabel = "Retry"
}) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-testid="error-state">
    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 max-w-md mb-4">{description}</p>
    {action && (
      <button
        onClick={action}
        data-testid="error-state-retry-btn"
        className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// Analysis Page Skeleton (combines header + content)
const AnalysisPageSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div>
        <div className="w-48 h-8 bg-gray-200 rounded mb-2" />
        <div className="w-32 h-4 bg-gray-200 rounded" />
      </div>
      <div className="flex gap-2">
        <div className="w-24 h-10 bg-gray-200 rounded-lg" />
        <div className="w-24 h-10 bg-gray-200 rounded-lg" />
      </div>
    </div>
    
    {/* KPI Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array(4).fill(0).map((_, i) => (
        <KPICardSkeleton key={i} />
      ))}
    </div>
    
    {/* Main Content */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="w-32 h-5 bg-gray-200 rounded mb-4" />
        <ChartSkeleton height={250} />
      </div>
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="w-32 h-5 bg-gray-200 rounded mb-4" />
        <TableSkeleton rows={5} columns={3} />
      </div>
    </div>
  </div>
);

// Dashboard Skeleton with KPIs and Charts
const DashboardSkeleton = () => (
  <div className="space-y-6 animate-pulse p-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div>
        <div className="w-64 h-8 bg-gray-200 rounded mb-2" />
        <div className="w-40 h-4 bg-gray-200 rounded" />
      </div>
      <div className="w-32 h-10 bg-gray-200 rounded-lg" />
    </div>
    
    {/* KPI Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array(4).fill(0).map((_, i) => (
        <KPICardSkeleton key={i} />
      ))}
    </div>
    
    {/* Action Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array(4).fill(0).map((_, i) => (
        <div key={i} className="p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gray-200 rounded-lg" />
            <div className="w-24 h-4 bg-gray-200 rounded" />
          </div>
          <div className="w-full h-4 bg-gray-200 rounded mb-2" />
          <div className="w-3/4 h-4 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
    
    {/* Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="w-40 h-5 bg-gray-200 rounded mb-4" />
        <ChartSkeleton height={300} />
      </div>
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="w-40 h-5 bg-gray-200 rounded mb-4" />
        <TableSkeleton rows={6} columns={4} />
      </div>
    </div>
  </div>
);

export { 
  CRPLLoadingScreen, 
  InlineLoader, 
  KPICardSkeleton,
  TableSkeleton,
  ChartSkeleton,
  CardGridSkeleton,
  EmptyState,
  ErrorState,
  AnalysisPageSkeleton,
  DashboardSkeleton
};
export default CRPLLoadingScreen;
